import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import {
  login,
  sendMagicLink,
  sendPasswordReset,
  clearMustChangePassword,
  supabase,
} from "@/lib/session";
import { friendlyError } from "@/lib/friendly-error";

type Mode =
  | "signin"
  | "magic-link"
  | "magic-sent"
  | "forgot"
  | "forgot-sent"
  | "recovery"        // user clicked email link, Supabase delivered a recovery session
  | "first-login";    // user signed in but users.must_change_password = true

export default function Login() {
  const [, navigate] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState<Mode>("signin");

  // Password reset / first-login fields
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [recoveryUserId, setRecoveryUserId] = useState<string>("");

  // Show/hide password toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Detect Supabase password-recovery event when user lands from the email link.
  // Supabase JS may emit PASSWORD_RECOVERY before this listener mounts, so we
  // also check sessionStorage (set by usePasswordRecoveryRoute) and the active
  // session to recover the recovery flow.
  useEffect(() => {
    const sb = supabase();
    let cancelled = false;

    // Pick up a recovery signal that may have been set before mount.
    let recoveryFlag = false;
    try { recoveryFlag = sessionStorage.getItem("aip:passwordRecovery") === "1"; } catch {}
    if (recoveryFlag) {
      sb.auth.getSession().then(({ data }) => {
        if (cancelled) return;
        if (data.session?.user) {
          setRecoveryUserId(data.session.user.id);
          setEmail(data.session.user.email || "");
          setMode("recovery");
          setError("");
          try { sessionStorage.removeItem("aip:passwordRecovery"); } catch {}
        }
      });
    }

    const { data: sub } = sb.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" && session?.user) {
        setRecoveryUserId(session.user.id);
        setEmail(session.user.email || "");
        setMode("recovery");
        setError("");
        try { sessionStorage.removeItem("aip:passwordRecovery"); } catch {}
      }
    });
    return () => { cancelled = true; sub.subscription.unsubscribe(); };
  }, []);

  function routeByRole(u: { role: string; isDualRole?: boolean; onboardingCompletedAt?: string | null }) {
    // W66.F: redirect to onboarding if not yet completed
    if (!u.onboardingCompletedAt) { navigate("/onboarding"); return; }
    let intended: string | null = null;
    try { intended = sessionStorage.getItem("aip:postLoginRedirect"); } catch {}
    if (intended) {
      try { sessionStorage.removeItem("aip:postLoginRedirect"); } catch {}
      navigate(intended);
      return;
    }
    if (u.role === "executive") navigate("/executive");
    else if (u.role === "marketing") navigate("/marketing");
    else if (u.role === "se") navigate("/dashboards/se");
    else if (u.role === "manager" && u.isDualRole) navigate("/executive");
    else if (u.role === "manager") navigate("/manager");
    else if (u.role === "ae") navigate("/my-plan");
    else if (u.role === "bdr") navigate("/my-plan");
    else navigate("/dashboards");
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email.trim().toLowerCase(), password);
      if (u.mustChangePassword) {
        setRecoveryUserId(u.id);
        setMode("first-login");
        return;
      }
      routeByRole(u);
    } catch (err: any) {
      const msg = err?.message || "Sign in failed";
      if (msg.toLowerCase().includes("password") || msg.toLowerCase().includes("credentials")) {
        setError("Incorrect email or password.");
      } else if (msg.toLowerCase().includes("confirm")) {
        setError("Check your email to confirm your account, then try again.");
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await sendMagicLink(email.trim().toLowerCase());
      setMode("magic-sent");
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await sendPasswordReset(email.trim().toLowerCase());
      setMode("forgot-sent");
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  // Used by both "recovery" (came from email link) and "first-login" (must_change_password=true) flows.
  async function handleSetNewPassword(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { error: updateError } = await supabase().auth.updateUser({ password: newPassword });
      if (updateError) throw new Error(updateError.message);

      if (recoveryUserId) {
        try { await clearMustChangePassword(recoveryUserId); } catch { /* non-fatal */ }
      }

      // Refresh the AIP session with the new password so we get a fresh users row + role.
      const u = await login(email.trim().toLowerCase(), newPassword);
      routeByRole(u);
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  // ── Sent confirmation screens ──────────────────────────────────────────
  if (mode === "magic-sent" || mode === "forgot-sent") {
    const isReset = mode === "forgot-sent";
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center space-y-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
            Mi6 Group · Attilius
          </div>
          <h1 className="text-xl font-semibold tracking-tight">Check your inbox</h1>
          <p className="text-sm text-muted-foreground">
            {isReset
              ? <>A password reset link was sent to <strong>{email}</strong>. Open it on this device to choose a new password.</>
              : <>A sign-in link was sent to <strong>{email}</strong>. Open it on this device to continue.</>}
          </p>
          <button
            onClick={() => { setMode("signin"); setError(""); }}
            className="text-[12px] text-muted-foreground underline underline-offset-2"
          >
            Back to sign in
          </button>
          <SupportFooter />
        </div>
      </div>
    );
  }

  // ── Set new password (recovery from email link OR first-login forced change) ──
  if (mode === "recovery" || mode === "first-login") {
    const isFirstLogin = mode === "first-login";
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
              Mi6 Group · Attilius
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {isFirstLogin ? "Set your password" : "Reset your password"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isFirstLogin
                ? "This is your first sign-in. Choose a permanent password before continuing."
                : "Choose a new password for your Attilius account."}
            </p>
          </div>
          <form onSubmit={handleSetNewPassword} className="card-shell p-6 space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">
                New password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="Min 8 characters"
                  className="w-full px-3 py-2 pr-10 rounded border border-border bg-background text-sm focus:outline-none focus:border-foreground"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((v) => !v)}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground px-1"
                >
                  {showNewPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">
                Confirm password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full px-3 py-2 pr-10 rounded border border-border bg-background text-sm focus:outline-none focus:border-foreground"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground px-1"
                >
                  {showConfirmPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            {error && (
              <div className="text-[12px] text-rose-600 border border-rose-200 bg-rose-50 rounded px-3 py-2">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full py-2 rounded bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-60 transition-opacity"
            >
              {busy ? "Saving…" : "Set password and continue"}
            </button>
          </form>
          <SupportFooter />
        </div>
      </div>
    );
  }

  // ── Forgot password (request email) ────────────────────────────────────
  if (mode === "forgot") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
              Mi6 Group · Attilius
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">Reset password</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Enter your email and we will send a password reset link.
            </p>
          </div>
          <form onSubmit={handleForgotPassword} className="card-shell p-6 space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">
                Email
              </label>
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="name@mi6group.com"
                className="w-full px-3 py-2 rounded border border-border bg-background text-sm focus:outline-none focus:border-foreground"
              />
            </div>
            {error && (
              <div className="text-[12px] text-rose-600 border border-rose-200 bg-rose-50 rounded px-3 py-2">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full py-2 rounded bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-60 transition-opacity"
            >
              {busy ? "Sending…" : "Send reset link"}
            </button>
            <div className="pt-1 text-center">
              <button
                type="button"
                onClick={() => { setMode("signin"); setError(""); }}
                className="text-[12px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                Back to sign in
              </button>
            </div>
          </form>
          <SupportFooter />
        </div>
      </div>
    );
  }

  // ── Default signin / magic-link entry ──────────────────────────────────
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {/* Mi6 Group / powered by Attilius — stacked partner lockup */}
          <div className="flex items-center justify-center mb-4">
            <img
              src="/mi6-login-lockup.png"
              alt="Mi6 Group · powered by Attilius"
              className="h-auto w-auto max-w-[280px] select-none"
              draggable={false}
            />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
          <p className="text-[11px] text-muted-foreground mt-2 italic">
            Works with your CRM. Replaces no one.
          </p>
        </div>

        {mode === "signin" && (
          <form
            onSubmit={handleSignIn}
            className="card-shell p-6 space-y-4"
            aria-label="Attilius sign in"
          >
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">
                Email
              </label>
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="name@mi6group.com"
                className="w-full px-3 py-2 rounded border border-border bg-background text-sm focus:outline-none focus:border-foreground"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-3 py-2 pr-12 rounded border border-border bg-background text-sm focus:outline-none focus:border-foreground"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground px-1"
                  data-testid="login-password-toggle"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <div className="pt-1 text-right">
                <button
                  type="button"
                  onClick={() => { setMode("forgot"); setError(""); }}
                  className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  data-testid="login-forgot"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            {error && (
              <div className="text-[12px] text-rose-600 border border-rose-200 bg-rose-50 rounded px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full py-2 rounded text-white text-sm font-medium hover:opacity-90 disabled:opacity-60 transition-opacity"
              style={{ backgroundColor: "#DC2626" }}
              data-testid="login-submit"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>

            <div className="pt-1 text-center">
              <button
                type="button"
                onClick={() => { setMode("magic-link"); setError(""); }}
                className="text-[12px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                Sign in with magic link instead
              </button>
            </div>
          </form>
        )}

        {mode === "magic-link" && (
          <form
            onSubmit={handleMagicLink}
            className="card-shell p-6 space-y-4"
            aria-label="Magic link sign in"
          >
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">
                Email
              </label>
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="name@mi6group.com"
                className="w-full px-3 py-2 rounded border border-border bg-background text-sm focus:outline-none focus:border-foreground"
              />
            </div>

            {error && (
              <div className="text-[12px] text-rose-600 border border-rose-200 bg-rose-50 rounded px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full py-2 rounded bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-60 transition-opacity"
            >
              {busy ? "Sending…" : "Send magic link"}
            </button>

            <div className="pt-1 text-center">
              <button
                type="button"
                onClick={() => { setMode("signin"); setError(""); }}
                className="text-[12px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                Back to password sign in
              </button>
            </div>
          </form>
        )}

        <SupportFooter />
      </div>
    </div>
  );
}

function SupportFooter() {
  return (
    <div className="mt-6 text-center text-[11px] text-muted-foreground space-y-1.5">
      <div>
        Need help? Contact{" "}
        <a
          href="mailto:support@mi6group.com"
          className="underline underline-offset-2 hover:text-foreground"
        >
          support@mi6group.com
        </a>
      </div>
      <div className="flex items-center justify-center gap-3">
        <a href="/#/legal/terms" className="underline underline-offset-2 hover:text-foreground">Terms</a>
        <span>·</span>
        <a href="/#/legal/privacy" className="underline underline-offset-2 hover:text-foreground">Privacy</a>
      </div>
    </div>
  );
}
